import {_} from 'lodash';
import * as ko from "knockout"
import {moment} from 'moment'
import {StatusManager} from "../../StatusManager";
import {global, lm} from "../../../../../appContext";
import {createErrorMessage} from "../../../../../util/misc";
import {PLEASE_WAIT_MESSAGE} from "../../../../../constants/Language";
import {IMessageData} from "../../../../../index";
import {convertToServerString} from "../../../../../util/datetime";
import {getHash} from "../../../../../constants/RouteDefinitions";
import {KOSubscriptionTracker} from "../../../../../util/KOSubscriptionTracker";
import {TBtnHeader} from "../..";
import {getTabHash} from "../../../../../util/pageShellRoute";
import {withHashQuery} from "../../../../../util/hashQuery";
import detailsHtml from './details.html?raw';

export const template = detailsHtml

export class viewModel {

    private readonly isReady: KnockoutObservable<boolean> = ko.observable(true)
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly buttons: KnockoutObservableArray<TBtnHeader> = ko.observableArray()
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()

    constructor(private readonly statusManager: StatusManager) {
        this.buttons([this.btnImportfile, this.btnTryAgain, this.btnLetChangesThrough])
        this.kosub.subscribe(this.statusManager.isSyncUpdated, bool => {
            if (bool) {
                // rerender component
                this.isReady(false)
                this.isReady(true)
            }
        })
    }

    private btnImportfile: TBtnHeader = {
        cls: 'btn-action',
        href: () => {
            const importfileid = this.statusManager.importfile.id;
            if (this.statusManager.getSyncCycleStatus() == 'executedwitherrors') {
                return withHashQuery(
                    getTabHash('importhistoryhistory', {importfileid}),
                    {showOnlyErrors: true},
                ) ?? '#'
            }
            return getHash('importhistorydetails', {importfileid}) ?? '#'
        },
        label: 'sync.status.btn.importfile',
        validator: () => !!this.statusManager.importfile?.id,
    }

    private btnTryAgain: TBtnHeader = {
        cls: 'btn-action',
        enabled: () => this.canReset(),
        onClick: () => this.onTryAgainClick(),
        label: 'sync.status.btn.tryagain',
        validator: () => this.canReset() || this.canResume(),
    }

    private btnLetChangesThrough: TBtnHeader = {
        cls: 'btn-action',
        enabled: () => this.canResume(),
        onClick: () => this.onLetChangesThroughClick(),
        label: 'sync.status.btn.letchangesthrough',
        validator: () => this.canReset() || this.canResume(),
    }

    private canReset(): boolean {
        const lastsyncstep = this.statusManager.company.lastsyncstep;
        if (!lastsyncstep) {
            return false
        }
        return this.isHaltedInSynchronization() && lastsyncstep.canreset
    }

    private canResume(): boolean {
        const lastsyncstep = this.statusManager.company.lastsyncstep;
        if (!lastsyncstep) {
            return false
        }
        return this.isHaltedInSynchronization() && lastsyncstep.canresume
    }

    private isHaltedInSynchronization(): boolean {
        const lastsyncstep = this.statusManager.company.lastsyncstep;
        if (!lastsyncstep) {
            return false;
        }
        const a = lastsyncstep.halted
        const b = lastsyncstep.syncstepname == 'Syncronization';
        const c = global.permissionValidator.isMinCompanyAdmin();
        return a && b && c
    }

    private onTryAgainClick() {
        global.dialogManager.addConfirm({
            title: 'sync.status.dialog.tryagain',
            content: lm.t('sync.status.dialog.tryagain.body') ?? '',
            buttons: {
                cancel: {
                    label: 'sync.status.dialog.tryagain.btn.cancel'
                },
                confirm: {
                    label: 'sync.status.dialog.tryagain.btn.confirm',
                    cls: 'btn-primary',
                    onClick: () => {
                        this.statusManager.isSubmitting(true)
                        this.messageData({message: PLEASE_WAIT_MESSAGE})
                        return Promise
                            .resolve()
                            .then(() => this.statusManager.do_deletelastsynccycle())
                            .then(() => {
                                this.statusManager.removeCache()
                                global.snackbarData({message: 'sync.status.dialog.tryagain.message.success'})
                            })
                            .then(() => this.statusManager.init())
                            .catch((reason: string) => {
                                global.snackbarData(createErrorMessage(String(reason)))
                            });

                    },
                }
            }
        });
    }

    private onLetChangesThroughClick() {
        global.dialogManager.addConfirm({
            title: `sync.status.dialog.letchangesthrough`,
            content: lm.t('sync.status.dialog.letchangesthrough.body') ?? '',
            buttons: {
                cancel: {
                    label: 'sync.status.dialog.letchangesthrough.btn.cancel',
                },
                confirm: {
                    label: 'sync.status.dialog.letchangesthrough.btn.confirm',
                    onClick: () => {
                        this.statusManager.isSubmitting(true)
                        this.messageData({message: PLEASE_WAIT_MESSAGE})
                        return Promise
                            .resolve()
                            .then(() => this.statusManager.do_resumesynccycle())
                            .then(() => this.statusManager.do_deletelastsyncstep())
                            .then(() => {
                                const m = moment().startOf('day').add(1, 'day')
                                // skip weekend (saturday and sunday)
                                while (m.isoWeekday() == 6 || m.isoWeekday() == 7) {
                                    m.add(1, 'day')
                                }
                                const props = this.statusManager.mutableCompany.props;
                                const converted = convertToServerString(m);
                                if (converted) {
                                    props.ignoreimportfilteruntil?.(converted);
                                }
                                props.ignoreimportfilter?.(true)
                                if (this.statusManager.company.deltasync) {
                                    // queuing delta sync
                                    props.syncableedits?.(true);
                                }
                                return this.statusManager.mutableCompany.submit(['ignoreimportfilteruntil', 'ignoreimportfilter', 'syncableedits'])
                            })
                            .then(() => {
                                global.snackbarData({message: 'sync.status.dialog.letchangesthrough.message.success'})
                                this.statusManager.removeCache('getcompany')
                            })
                            .then(() => this.statusManager.init())
                            .catch((reason: string) => {
                                this.statusManager.isSubmitting(false)
                                global.snackbarData(createErrorMessage(String(reason)));
                            })
                    },
                }
            }
        })
    }

    private getDate() {
        const ended = this.statusManager?.importfile?.completed ?? this.statusManager?.importfile?.ended
        if (ended) {
            return ended
        }
        const started = this.statusManager?.importfile?.started
        if (started) {
            return started
        }
        const firstSynccycleStep = _.first(this.statusManager?.importfile?.synccyclesteps)
        if (firstSynccycleStep?.started) {
            return firstSynccycleStep.started
        }
        return null
    }

    dispose() {
        this.kosub.unsuscribeAll()
    }

}
